/**
 * Sync Status Indicator Component
 * Shows cloud sync status with visual feedback
 */

import React, { useState, useEffect } from 'react';
import { Cloud, CloudOff, Check, Loader2, AlertCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';

type SyncState = 'synced' | 'syncing' | 'pending' | 'error' | 'offline';

interface SyncStatusIndicatorProps {
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ 
  showLabel = false,
  size = 'md' 
}) => {
  const [syncState, setSyncState] = useState<SyncState>('synced');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showTooltip, setShowTooltip] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date>(new Date());

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setSyncState('synced');
      setLastSyncTime(new Date());
    };
    const handleOffline = () => {
      setIsOnline(false);
      setSyncState('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for custom sync events
  useEffect(() => {
    const handleSyncStart = () => setSyncState('syncing');
    const handleSyncComplete = () => {
      setSyncState('synced');
      setLastSyncTime(new Date());
    };
    const handleSyncError = () => setSyncState('error');

    window.addEventListener('sync:start', handleSyncStart);
    window.addEventListener('sync:complete', handleSyncComplete);
    window.addEventListener('sync:error', handleSyncError);

    return () => {
      window.removeEventListener('sync:start', handleSyncStart);
      window.removeEventListener('sync:complete', handleSyncComplete);
      window.removeEventListener('sync:error', handleSyncError);
    };
  }, []);

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const iconSize = size === 'sm' ? 14 : size === 'md' ? 18 : 22;

  const getStatusConfig = (state: SyncState) => {
    switch (state) {
      case 'synced':
        return {
          icon: <Cloud className={`${sizeClasses[size]} text-emerald-500`} />,
          overlay: <Check size={iconSize * 0.5} className="absolute -bottom-0.5 -right-0.5 text-emerald-500 bg-white dark:bg-stone-900 rounded-full" />,
          label: 'Synced',
          labelHindi: 'सिंक हो गया',
          color: 'text-emerald-500',
          bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
          borderColor: 'border-emerald-200 dark:border-emerald-800'
        };
      case 'syncing':
        return {
          icon: <Loader2 className={`${sizeClasses[size]} text-blue-500 animate-spin`} />,
          overlay: null,
          label: 'Syncing...',
          labelHindi: 'सिंक हो रहा है...',
          color: 'text-blue-500',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800'
        };
      case 'pending':
        return {
          icon: <Cloud className={`${sizeClasses[size]} text-amber-500`} />,
          overlay: <RefreshCw size={iconSize * 0.5} className="absolute -bottom-0.5 -right-0.5 text-amber-500 bg-white dark:bg-stone-900 rounded-full" />,
          label: 'Pending',
          labelHindi: 'बाकी है',
          color: 'text-amber-500',
          bgColor: 'bg-amber-50 dark:bg-amber-900/20',
          borderColor: 'border-amber-200 dark:border-amber-800'
        };
      case 'error':
        return {
          icon: <Cloud className={`${sizeClasses[size]} text-red-500`} />,
          overlay: <AlertCircle size={iconSize * 0.5} className="absolute -bottom-0.5 -right-0.5 text-red-500 bg-white dark:bg-stone-900 rounded-full" />,
          label: 'Sync Error',
          labelHindi: 'सिंक एरर',
          color: 'text-red-500',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800'
        };
      case 'offline':
        return {
          icon: <WifiOff className={`${sizeClasses[size]} text-gray-400`} />,
          overlay: null,
          label: 'Offline',
          labelHindi: 'ऑफलाइन',
          color: 'text-gray-400',
          bgColor: 'bg-gray-50 dark:bg-gray-900/20',
          borderColor: 'border-gray-200 dark:border-gray-700'
        };
    }
  };

  const config = getStatusConfig(syncState);

  const formatLastSync = () => {
    if (!lastSyncTime) return null;
    
    const now = new Date();
    const diff = now.getTime() - lastSyncTime.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'अभी';
    if (minutes < 60) return `${minutes} मिनट पहले`;
    if (hours < 24) return `${hours} घंटे पहले`;
    return lastSyncTime.toLocaleDateString('hi-IN');
  };

  return (
    <div 
      className="relative inline-flex items-center gap-1.5"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Icon with overlay */}
      <div className="relative">
        {config.icon}
        {config.overlay}
      </div>

      {/* Label (optional) */}
      {showLabel && (
        <span className={`text-xs font-medium ${config.color}`}>
          {config.labelHindi}
        </span>
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 min-w-[160px] ${config.bgColor} ${config.borderColor} border rounded-lg shadow-lg p-2 text-xs`}>
          {/* Arrow */}
          <div className={`absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 ${config.bgColor} ${config.borderColor} border-l border-t rotate-45`} />
          
          <div className="relative">
            {/* Status */}
            <div className={`font-medium ${config.color} flex items-center gap-1`}>
              {config.icon}
              {config.labelHindi}
            </div>
            
            {/* Last sync time */}
            {syncState === 'synced' && lastSyncTime && (
              <div className="text-gray-500 dark:text-stone-400 mt-1">
                आखिरी सिंक: {formatLastSync()}
              </div>
            )}
            
            {/* Offline message */}
            {syncState === 'offline' && (
              <div className="text-gray-400 mt-1">
                इंटरनेट कनेक्शन नहीं है
              </div>
            )}
            
            {/* Error message */}
            {syncState === 'error' && (
              <div className="text-red-400 mt-1 text-[10px]">
                कुछ गड़बड़ हुई। फिर से कोशिश करें।
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncStatusIndicator;
